import { useEffect, useState, useRef } from 'react';
import { Select, SelectItem } from '@nextui-org/react';
import { SEASONS } from 'common/src/utils/definitions';
import useFilterStore from '../../stores/useFiltersStore';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../hooks/useTranslation';

interface SeasonFilterProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  disabled?: boolean;
  collapsed?: boolean;
}

const SeasonFilter = ({
  className = 'min-w-32',
  size = 'sm',
  label,
  disabled = false,
  collapsed = false,
}: SeasonFilterProps) => {
  const { t } = useTranslation('common');
  const { selectedSeason, setSelectedSeason } = useFilterStore();
  const [showDropdown, setShowDropdown] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const defaultLabel = label || t('Temporada');

  // If no season is selected, select the latest one
  useEffect(() => {
    if (!selectedSeason) {
      const seasons = Object.values(SEASONS);
      setSelectedSeason(seasons[seasons.length - 1].name);
    }
  }, [selectedSeason, setSelectedSeason]);

  // Calculate dropdown position when showing
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top,
        left: rect.right + 10, // 10px to the right of the button
      });
    }
  }, [showDropdown]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('.season-dropdown')
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle season change
  const handleSeasonChange = (value: string) => {
    setSelectedSeason(value);
    setShowDropdown(false);
  };

  // Convert season name to short format (24-25)
  const getShortSeasonName = (seasonName: string): string => {
    const match = seasonName.match(/(\d{4})-(\d{4})/);
    if (match) {
      const firstYear = match[1].slice(-2);
      const secondYear = match[2].slice(-2);
      return `${firstYear}-${secondYear}`;
    }
    return seasonName; // fallback
  };

  // Portal-rendered dropdown
  const Dropdown = () => {
    if (!showDropdown) return null;

    return createPortal(
      <div
        className='season-dropdown fixed py-1 w-auto min-w-[150px] bg-background rounded-md shadow-lg z-[999999] border border-divider'
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
        }}
      >
        {Object.values(SEASONS).map((season) => (
          <div
            key={season.name}
            className={`px-4 py-2 text-sm cursor-pointer hover:bg-default-200 flex items-center ${selectedSeason === season.name ? 'bg-default-100 font-medium' : ''
              }`}
            onClick={() => handleSeasonChange(season.name)}
          >
            <span>{season.name}</span>
          </div>
        ))}
      </div>,
      document.body
    );
  };

  // Render collapsed circular selector
  if (collapsed) {
    const shortSeason = selectedSeason ? getShortSeasonName(selectedSeason) : '24-25';

    return (
      <div className='relative'>
        <div
          ref={buttonRef}
          className='w-10 h-10 rounded-full flex items-center justify-center bg-background border border-divider shadow-sm cursor-pointer hover:bg-default-100'
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <span
            className='text-xs font-bold text-default-600'
            aria-label={`${t('Temporada')} ${selectedSeason || t('actual')}`}
          >
            {shortSeason}
          </span>
        </div>

        <Dropdown />
      </div>
    );
  }

  // Render normal selector when not collapsed
  return (
    <div className='relative w-full'>
      <Select
        label={defaultLabel}
        selectedKeys={[selectedSeason]}
        onChange={(e) => setSelectedSeason(e.target.value)}
        className={className}
        size={size}
        aria-label={t('Selecciona temporada')}
        isDisabled={disabled}
      >
        {Object.values(SEASONS).map((season) => (
          <SelectItem key={season.name} value={season.name}>
            {season.name}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
};

export default SeasonFilter;
